import React, { useEffect, useState } from "react";
import {
  Button,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { doc, getDoc } from "firebase/firestore";
import { ContractService } from "./Master_ContractService";
import { Contract, Device, LoginUser } from "./web_parts/types";
import { db } from "../../firebase/firebase";

const { Option } = Select;

const emptyFilterConditions = {
  email: "",
  displayName: "",
  userId: "",
  deviceId: "",
  isActive: null as boolean | null,
};

const formatTs = (ts: number) =>
  ts > 0 ? new Date(ts).toLocaleString("ja-JP") : "-";

/**
 * 管理者セットアップ履歴（旧・契約管理）
 * 端末初期セットアップで登録された管理者のみを閲覧する。
 */
const Master_Contract: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [filteredData, setFilteredData] = useState<Contract[]>([]);
  const [maxRecords, setMaxRecords] = useState<number>(50);
  const [isFilterDialogVisible, setIsFilterDialogVisible] = useState(false);
  const [filterConditions, setFilterConditions] = useState(emptyFilterConditions);

  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [deviceModalLoading, setDeviceModalLoading] = useState(false);
  const [deviceDetail, setDeviceDetail] = useState<Device | null>(null);
  const [deviceMissingId, setDeviceMissingId] = useState<string | null>(null);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<LoginUser | null>(null);
  const [userMissingId, setUserMissingId] = useState<string | null>(null);

  const applyFilterTo = (list: Contract[], conditions: typeof emptyFilterConditions) => {
    let filtered = list;
    if (conditions.email) {
      const q = conditions.email.toLowerCase();
      filtered = filtered.filter((c) => (c.email ?? "").toLowerCase().includes(q));
    }
    if (conditions.displayName) {
      const q = conditions.displayName.toLowerCase();
      filtered = filtered.filter((c) => (c.displayName ?? "").toLowerCase().includes(q));
    }
    if (conditions.userId) {
      const q = conditions.userId.toLowerCase();
      filtered = filtered.filter((c) => (c.userId ?? "").toLowerCase().includes(q));
    }
    if (conditions.deviceId) {
      const q = conditions.deviceId.toLowerCase();
      filtered = filtered.filter((c) => (c.deviceId ?? "").toLowerCase().includes(q));
    }
    if (conditions.isActive !== null) {
      filtered = filtered.filter((c) => c.isActive === conditions.isActive);
    }
    return filtered;
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const contractList = await ContractService.getContracts();
      setContracts(contractList);
      setFilteredData(applyFilterTo(contractList, filterConditions));
    } catch (error) {
      console.error("管理者登録履歴の読み込みエラー:", error);
      message.error("管理者登録履歴の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadInitialData();
  }, []);

  const applyFilter = () => {
    const filtered = applyFilterTo(contracts, filterConditions);
    setFilteredData(filtered);
    setIsFilterDialogVisible(false);
    message.success(`フィルタを適用しました。${filtered.length}件が表示されます。`);
  };

  const clearAllFilters = () => {
    setFilterConditions(emptyFilterConditions);
    setFilteredData(contracts);
    message.success("フィルタを解除しました。");
  };

  const openDeviceDetail = async (deviceId: string) => {
    if (!deviceId) return;
    setDeviceModalOpen(true);
    setDeviceModalLoading(true);
    setDeviceDetail(null);
    setDeviceMissingId(null);
    try {
      const snap = await getDoc(doc(db, "devices", deviceId));
      if (!snap.exists() || snap.data()?.isDeleted === true) {
        setDeviceMissingId(deviceId);
        return;
      }
      const data = snap.data() as Record<string, unknown>;
      setDeviceDetail({
        id: snap.id,
        serialNumber: snap.id,
        isDeleted: false,
        deviceName: typeof data.deviceName === "string" ? data.deviceName : undefined,
        managerEmail: typeof data.managerEmail === "string" ? data.managerEmail : undefined,
        userDisplayName: typeof data.userDisplayName === "string" ? data.userDisplayName : undefined,
        plan: data.plan === "standard_plan" ? "standard_plan" : data.plan === "free" ? "free" : undefined,
        expiry: typeof data.expiry === "number" ? data.expiry : undefined,
        appVersion: typeof data.appVersion === "string" ? data.appVersion : undefined,
        lastSyncTime: typeof data.lastSyncTime === "number" ? data.lastSyncTime : undefined,
        osVersion: typeof data.osVersion === "string" ? data.osVersion : undefined,
        model: typeof data.model === "string" ? data.model : undefined,
      });
    } catch {
      message.error("デバイス情報の取得に失敗しました");
      setDeviceMissingId(deviceId);
    } finally {
      setDeviceModalLoading(false);
    }
  };

  const openUserDetail = async (userId: string, fallback?: Contract) => {
    if (!userId) return;
    setUserModalOpen(true);
    setUserModalLoading(true);
    setUserDetail(null);
    setUserMissingId(null);
    try {
      const snap = await getDoc(doc(db, "loginusers", userId));
      if (!snap.exists()) {
        setUserMissingId(userId);
        if (fallback) {
          setUserDetail({
            id: userId,
            email: fallback.email,
            displayName: fallback.displayName,
            role: "standard",
            createdAt: fallback.createdAt,
          });
        }
        return;
      }
      const data = snap.data() as Record<string, unknown>;
      setUserDetail({
        id: snap.id,
        email: typeof data.email === "string" ? data.email : fallback?.email ?? "",
        displayName:
          typeof data.displayName === "string"
            ? data.displayName
            : fallback?.displayName ?? "",
        role: data.role === "admin" ? "admin" : "standard",
        phoneNumber: typeof data.phoneNumber === "string" ? data.phoneNumber : "",
        createdAt: typeof data.createdAt === "number" ? data.createdAt : fallback?.createdAt ?? 0,
        updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : undefined,
        lastLoginAt: typeof data.lastLoginAt === "number" ? data.lastLoginAt : null,
      });
    } catch {
      message.error("ユーザー情報の取得に失敗しました");
      setUserMissingId(userId);
    } finally {
      setUserModalLoading(false);
    }
  };

  const columns: ColumnsType<Contract> = [
    {
      title: "メールアドレス",
      dataIndex: "email",
      key: "email",
      width: 220,
      ellipsis: true,
      render: (email: string, record) =>
        record.isActive ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => void openUserDetail(record.userId, record)}>
            {email || "-"}
          </Button>
        ) : (
          email || "-"
        ),
    },
    {
      title: "表示名",
      dataIndex: "displayName",
      key: "displayName",
      width: 140,
      ellipsis: true,
    },
    {
      title: "登録回数",
      dataIndex: "setupCount",
      key: "setupCount",
      width: 100,
      sorter: (a, b) => a.setupCount - b.setupCount,
    },
    {
      title: "セットアップ時刻",
      dataIndex: "lastSetupAt",
      key: "lastSetupAt",
      width: 170,
      render: (ts: number) => formatTs(ts),
      sorter: (a, b) => a.lastSetupAt - b.lastSetupAt,
    },
    {
      title: "有効",
      dataIndex: "isActive",
      key: "isActive",
      width: 90,
      render: (active: boolean | undefined) =>
        active ? <Tag color="green">有効</Tag> : <Tag>無効</Tag>,
    },
    {
      title: "デバイスID",
      dataIndex: "deviceId",
      key: "deviceId",
      width: 160,
      ellipsis: true,
      render: (deviceId: string, record) =>
        record.isActive && deviceId ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => void openDeviceDetail(deviceId)}>
            {deviceId}
          </Button>
        ) : (
          deviceId || "-"
        ),
    },
    {
      title: "ユーザーID",
      dataIndex: "userId",
      key: "userId",
      width: 200,
      ellipsis: true,
      render: (userId: string, record) =>
        record.isActive ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => void openUserDetail(userId, record)}>
            {userId || "-"}
          </Button>
        ) : (
          userId || "-"
        ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 12, color: "#595959", lineHeight: 1.6 }}>
        端末の初期セットアップで「管理者」として登録された履歴です（1回のセットアップ＝1件）。
        同じメールで再セットアップすると履歴が増え、最新のみ「有効」になります。
        デバイス／ユーザーへのリンクは「有効」の行だけです。
      </div>

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void loadInitialData()} loading={loading}>
            更新
          </Button>
        </Space>
        <Space wrap>
          <Button onClick={() => setIsFilterDialogVisible(true)}>フィルタ</Button>
          <Button onClick={clearAllFilters}>フィルタ解除</Button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>最大件数:</span>
            <Select value={maxRecords} style={{ width: 100 }} onChange={(value) => setMaxRecords(value)}>
              <Option value={10}>10件</Option>
              <Option value={25}>25件</Option>
              <Option value={50}>50件</Option>
              <Option value={100}>100件</Option>
              <Option value={-1}>全件</Option>
            </Select>
          </div>
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        scroll={{ x: 1100 }}
        style={{ width: "100%" }}
        pagination={{
          pageSize: maxRecords === -1 ? filteredData.length || 1 : maxRecords,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <Modal
        title="フィルタ条件設定"
        open={isFilterDialogVisible}
        onCancel={() => setIsFilterDialogVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <label>メールアドレス:</label>
          <Input
            value={filterConditions.email}
            onChange={(e) => setFilterConditions({ ...filterConditions, email: e.target.value })}
            placeholder="メールで検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>表示名:</label>
          <Input
            value={filterConditions.displayName}
            onChange={(e) => setFilterConditions({ ...filterConditions, displayName: e.target.value })}
            placeholder="表示名で検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>UserId:</label>
          <Input
            value={filterConditions.userId}
            onChange={(e) => setFilterConditions({ ...filterConditions, userId: e.target.value })}
            placeholder="UserIdで検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>deviceId:</label>
          <Input
            value={filterConditions.deviceId}
            onChange={(e) => setFilterConditions({ ...filterConditions, deviceId: e.target.value })}
            placeholder="deviceIdで検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>有効:</label>
          <Select
            allowClear
            style={{ width: "100%", marginTop: 4 }}
            value={filterConditions.isActive}
            onChange={(value) =>
              setFilterConditions({
                ...filterConditions,
                isActive: typeof value === "boolean" ? value : null,
              })
            }
            placeholder="すべて"
          >
            <Option value={true}>有効</Option>
            <Option value={false}>無効</Option>
          </Select>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={() => setIsFilterDialogVisible(false)}>キャンセル</Button>
          <Button type="primary" onClick={applyFilter}>
            適用
          </Button>
        </div>
      </Modal>

      <Modal
        title="デバイス情報"
        open={deviceModalOpen}
        onCancel={() => setDeviceModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDeviceModalOpen(false)}>
            閉じる
          </Button>,
        ]}
        destroyOnClose
      >
        {deviceModalLoading ? (
          <Typography.Text>読み込み中...</Typography.Text>
        ) : deviceDetail ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="deviceId">{deviceDetail.id}</Descriptions.Item>
            <Descriptions.Item label="デバイス名">{deviceDetail.deviceName || "-"}</Descriptions.Item>
            <Descriptions.Item label="管理者メール">{deviceDetail.managerEmail || "-"}</Descriptions.Item>
            <Descriptions.Item label="ユーザー名">{deviceDetail.userDisplayName || "-"}</Descriptions.Item>
            <Descriptions.Item label="プラン">{deviceDetail.plan || "-"}</Descriptions.Item>
            <Descriptions.Item label="有効期限">
              {deviceDetail.expiry ? new Date(deviceDetail.expiry).toLocaleDateString("ja-JP") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="appVersion">{deviceDetail.appVersion || "-"}</Descriptions.Item>
            <Descriptions.Item label="OS">{deviceDetail.osVersion || "-"}</Descriptions.Item>
            <Descriptions.Item label="モデル">{deviceDetail.model || "-"}</Descriptions.Item>
            <Descriptions.Item label="最終同期">
              {deviceDetail.lastSyncTime
                ? new Date(deviceDetail.lastSyncTime).toLocaleString("ja-JP")
                : "-"}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">
            デバイスが見つからないか、削除済みです（{deviceMissingId || "-"}）。
          </Typography.Text>
        )}
      </Modal>

      <Modal
        title="ユーザー情報"
        open={userModalOpen}
        onCancel={() => setUserModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setUserModalOpen(false)}>
            閉じる
          </Button>,
        ]}
        destroyOnClose
      >
        {userModalLoading ? (
          <Typography.Text>読み込み中...</Typography.Text>
        ) : userDetail ? (
          <>
            {userMissingId && (
              <Typography.Paragraph type="warning">
                loginusers にドキュメントがありません（履歴上の情報を表示しています）。
              </Typography.Paragraph>
            )}
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="UserId">{userDetail.id}</Descriptions.Item>
              <Descriptions.Item label="表示名">{userDetail.displayName || "-"}</Descriptions.Item>
              <Descriptions.Item label="メール">{userDetail.email || "-"}</Descriptions.Item>
              <Descriptions.Item label="ロール">
                {userDetail.role === "admin" ? "管理者" : "一般"}
              </Descriptions.Item>
              <Descriptions.Item label="電話番号">{userDetail.phoneNumber || "-"}</Descriptions.Item>
              <Descriptions.Item label="登録日">{formatTs(userDetail.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新日">{formatTs(userDetail.updatedAt ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="最終ログイン">
                {formatTs(userDetail.lastLoginAt ?? 0)}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Typography.Text type="secondary">
            ユーザー情報を取得できませんでした（{userMissingId || "-"}）。
          </Typography.Text>
        )}
      </Modal>
    </>
  );
};

export default Master_Contract;
